import React, { ChangeEvent, useLayoutEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import { Common } from '@thecvlb/design-system';
import classNames from 'classnames';

import { notifyError } from 'shared/Toast/Toast';

import { useWidth } from 'hooks/useWidth';
import { fileToDataUrl } from 'utils/file';

import { ChatFormAttachment, ChatFormProps } from './chatForm.types';
import { ACCEPT_CHAT_MIMETYPES, fileValidator } from './chatForm.settings';

/**
 * Chat input with text and file upload support, including preview rendering.
 *
 * @param props - Submit handler, styling overrides, and disabled state toggles.
 * @returns Chat form UI.
 */
const ChatForm: React.FC<ChatFormProps> = ({
  acceptFileTypes = ACCEPT_CHAT_MIMETYPES.join(','),
  attachments,
  canSubmit,
  disabledSubmit,
  disableFileUpload = false,
  hideFileUpload = false,
  messagePlaceholder = 'Message...',
  onAddFiles,
  onRemoveAttachment,
  onSubmit,
  onValueChange,
  value,
  wrapperClasses
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [previews, setPreviews] = useState<{ image: string; name: string; type: string }[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [internalMessage, setInternalMessage] = useState<string>('');
  const { width } = useWindowSize();
  const { isMobile } = useWidth();
  const hasExternalAttachments = !!attachments;
  const message = value ?? internalMessage;
  const resolvedAttachments: ChatFormAttachment[] =
    attachments ??
    previews.map((preview, index) => ({
      id: index.toString(),
      name: preview.name,
      previewUrl: preview.image,
      type: preview.type
    }));

  const limitAvailableFiles = 10;

  const onClosePreview = () => {
    setPreviews([]);
    setFiles([]);
  };

  const reset = () => {
    onClosePreview();
    if (value === undefined) {
      setInternalMessage('');
    }
    onValueChange?.('');
  };

  const onChangeFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;
    if (onAddFiles) {
      onAddFiles(selectedFiles);
      e.target.value = '';
      return;
    }

    const { errVal, isValidFile } = fileValidator(selectedFiles);
    if (!isValidFile) {
      if (errVal) notifyError(errVal);
      return false;
    }

    try {
      const converted = await Promise.all(
        [...selectedFiles].map((file) => fileToDataUrl(file, { convertHeicToJpeg: true }))
      );

      setPreviews((prev) =>
        [
          ...prev,
          ...converted.map(({ dataUrl, file }) => ({
            image: dataUrl,
            name: file.name,
            type: file.type
          }))
        ]
          .filter((v, i, a) => a.findIndex((v2) => v2.name === v.name) === i)
          .slice(0, limitAvailableFiles)
      );

      setFiles((prev) =>
        [...prev, ...converted.map(({ file }) => file)]
          .filter((v, i, a) => a.findIndex((v2) => v2.name === v.name) === i)
          .slice(0, limitAvailableFiles)
      );
    } finally {
      e.target.value = '';
    }
  };

  const onChangeTextArea = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;

    if (value === undefined) {
      setInternalMessage(nextValue);
    }

    onValueChange?.(nextValue);
  };

  const removeAttachment = (id: string) => {
    if (hasExternalAttachments) {
      onRemoveAttachment?.(id);
      return;
    }

    const index = Number(id);
    if (Number.isNaN(index)) {
      return;
    }

    setPreviews((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    setFiles((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleOnSubmit = () => {
    if (disabledSubmit) return;
    onSubmit(message, files, reset);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleOnSubmit();
    }
  };

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight || 29}px`;
  }, [width, message]);

  const canSend =
    canSubmit ?? Boolean(message.trim().length || files.length || attachments?.length);

  return (
    <div className={wrapperClasses}>
      <div
        className={classNames(
          'border-gray-200 bg-white p-2 md:p-1',
          !isMobile ? 'rounded-xl border shadow-sm' : 'border-t'
        )}
        data-testid="chat_form"
      >
        {resolvedAttachments.length > 0 && (
          <div className="mb-2 flex gap-3 overflow-auto text-gray-700 md:mx-2 md:mb-0">
            {resolvedAttachments.map((attachment) => (
              <div
                key={attachment.id}
                className={classNames(
                  'relative mb-1 mt-1.5 flex-none rounded-xl md:mb-2.5 md:mt-2',
                  {
                    'flex max-w-[150px] items-center gap-1 truncate bg-green-100 p-3 text-mBase font-medium md:text-base':
                      !attachment.type.includes('image'),
                    'mb-2.5': !isMobile
                  }
                )}
              >
                <button
                  aria-label={`Remove ${attachment.name}`}
                  className={classNames(
                    'rounded-full border border-white bg-gray-700 p-0.5 text-white',
                    {
                      'absolute -right-1 -top-1.5 z-10': attachment.type.includes('image')
                    }
                  )}
                  type="button"
                  onClick={() => removeAttachment(attachment.id)}
                >
                  <Common.Icon className="size-3" name="close" />
                </button>
                {attachment.type.includes('image') ? (
                  <div className="relative">
                    <img
                      alt={attachment.name}
                      className="size-[52px] truncate rounded-xl object-cover shadow-sm"
                      src={attachment.previewUrl}
                    />
                    {attachment.statusLabel && (
                      <span className="absolute inset-x-0 bottom-0 rounded-b-xl bg-white/90 px-1 py-0.5 text-center text-[10px] font-medium text-gray-700">
                        {attachment.statusLabel}
                      </span>
                    )}
                  </div>
                ) : (
                  <>
                    <Common.Icon className="size-6 flex-none" name="articles" />
                    <span className="truncate rounded-xl object-cover">{attachment.name}</span>
                    {attachment.statusLabel && (
                      <span className="text-xs text-gray-700">{attachment.statusLabel}</span>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="flex min-w-0 items-center">
          <div className="flex min-w-0 flex-1 items-center">
            {!hideFileUpload && (
              <label
                className={classNames(
                  'cursor-pointer text-gray',
                  {
                    'cursor-not-allowed opacity-50': disableFileUpload
                  },
                  'border-r p-2 md:p-1.5 md:pr-3 md:pl-2'
                )}
              >
                <Common.Icon name="camera" />
                <input
                  accept={acceptFileTypes}
                  className="hidden"
                  data-testid="file_upload_clip"
                  disabled={disableFileUpload}
                  type="file"
                  multiple
                  onChange={onChangeFile}
                />
              </label>
            )}
            <textarea
              className={classNames(
                'max-h-[300px] min-w-0 flex-1 resize-none border-none py-0.5 text-gray-700 outline-none placeholder:text-gray focus:ring-0 md:text-base'
              )}
              data-testid="message_field"
              placeholder={messagePlaceholder}
              ref={ref}
              rows={1}
              value={message}
              onChange={onChangeTextArea}
              onKeyDown={handleKeyPress}
            />
          </div>
          <button
            className={classNames(
              'ml-2.5 rounded-lg bg-secondary p-1.5 px-3.5 text-white hover:bg-secondary-600 disabled:bg-gray-200 disabled:text-gray md:rounded-lg'
            )}
            data-testid="send_msg"
            disabled={!canSend || disabledSubmit}
            onClick={() => handleOnSubmit()}
          >
            <Common.Icon className="size-6 md:size-5" name="send" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatForm;
